import { Formik, Form } from "formik";
import { usePostHog } from "posthog-js/react";
import { useCallback, useMemo } from "react";
import * as Yup from "yup";
import { AdvocateSelector } from "@/core/components/advocate-selector";
import { FormikCheckboxWithLabel } from "@/core/components/checkbox-with-label";
import { FormInput } from "@/core/components/form-input";
import { SignupSourceSelector } from "@/core/components/signup-source-selector";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/core/components/ui/dialog";
import { PhoneInput } from "@/core/components/ui/phone-input";
import { Separator } from "@/core/components/ui/separator";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { useBusiness } from "@/core/business";
import { normalizeSignupSource } from "@/core/utils/form";
import { cn } from "@/core/utils/utils";
import { trackReferralCreation } from "@/referrals/events";
import { useCreateReferral } from "@/referrals/query";
import { IReferral } from "@/referrals/referral";
import { useGetMyUser } from "@/user/query";

const referralSchema = Yup.object().shape({
  service_needed: Yup.string().required("Service name is required"),
  advocate_id: Yup.string().when("isAddingNewAdvocate", {
    is: false,
    then: (schema) => schema.required("Advocate is required"),
  }),
  advocate_name: Yup.string().when("isAddingNewAdvocate", {
    is: true,
    then: (schema) => schema.required("Advocate name is required"),
  }),
  advocate_phone: Yup.string().when("isAddingNewAdvocate", {
    is: true,
    then: (schema) =>
      schema
        .required("Phone number is required")
        .matches(
          /^\(\d{3}\) \d{3}-\d{4}$/,
          "Please enter a valid phone number: (XXX) XXX-XXXX",
        )
        .test(
          "is-complete",
          "Please enter a complete phone number",
          (value) => {
            if (!value) return false;
            const digitCount = value.replace(/\D/g, "").length;
            return digitCount === 10;
          },
        ),
  }),
  signup_source_id: Yup.string(),
  signup_source_type: Yup.string(),
  customer_name: Yup.string(),
  customer_phone: Yup.string()
    .required("Phone number is required")
    .matches(
      /^\(\d{3}\) \d{3}-\d{4}$/,
      "Please enter a valid phone number: (XXX) XXX-XXXX",
    )
    .test("is-complete", "Please enter a complete phone number", (value) => {
      if (!value) return false;
      const digitCount = value.replace(/\D/g, "").length;
      return digitCount === 10;
    }),
  isAddingNewAdvocate: Yup.boolean(),
  onboard: Yup.boolean(),
  spiffable: Yup.boolean(),
});

const initialValues = {
  service_needed: "",
  advocate_id: "",
  advocate_name: "",
  advocate_phone: "",
  signup_source_id: "none",
  signup_source_type: "none",
  customer_name: "",
  customer_phone: "",
  isAddingNewAdvocate: false,
  onboard: false,
  spiffable: false,
};

interface AddReferralModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (data: IReferral) => void;
  onError?: (error: Error) => void;
  trigger?: React.ReactNode;
  businessId?: string;
}

const AddReferralModal = ({
  open,
  onOpenChange,
  onSuccess,
  onError,
  trigger,
  businessId,
}: AddReferralModalProps) => {
  const posthog = usePostHog();

  const { businessId: userBusinessId } = useBusiness();

  const { data: userInfo } = useGetMyUser();

  const effectiveBusinessId = businessId || (userBusinessId ?? undefined);

  const { mutate: createReferral } = useCreateReferral(effectiveBusinessId);

  const memoizedInitialValues = useMemo(
    () => ({
      ...initialValues,
      signup_source_id: userInfo?.id || "none",
      signup_source_type: "User",
    }),
    [userInfo?.id],
  );

  const handleSubmit = useCallback(
    (values: typeof initialValues) => {
      // Ensure both signup source fields are set or neither is set
      const normalizedSignupSource = normalizeSignupSource(
        values.signup_source_id,
        values.signup_source_type,
      );

      const payload = {
        ...values,
        business_id: effectiveBusinessId || (userInfo?.parentable_id as string),
        advocate: values.isAddingNewAdvocate
          ? {
              name: values.advocate_name,
              phone_number: values.advocate_phone,
              ...normalizedSignupSource,
              onboard: values.onboard,
              spiffable: normalizedSignupSource.signup_source_id
                ? values.spiffable
                : false,
            }
          : undefined,
        advocate_id: !values.isAddingNewAdvocate
          ? values.advocate_id
          : undefined,
      };
      createReferral(payload, {
        onSuccess: (response: IReferral) => {
          trackReferralCreation(posthog, {
            success: true,
            data: values,
          });
          toast.success("Referral created successfully");
          onSuccess?.(response);
          onOpenChange(false);
        },
        onError: (error) => {
          trackReferralCreation(posthog, {
            success: false,
            data: values,
          });
          toast.error("Failed to create referral");
          onError?.(error);
        },
      });
    },
    [
      createReferral,
      effectiveBusinessId,
      userInfo?.parentable_id,
      onSuccess,
      onError,
      onOpenChange,
      posthog,
    ],
  );

  return (
    <Formik
      initialValues={memoizedInitialValues}
      validationSchema={referralSchema}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        handleSubmit(values);
        resetForm();
        setSubmitting(false);
      }}
      validateOnMount
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        getFieldProps,
        setFieldValue,
        isSubmitting,
        isValid,
      }) => (
        <Dialog
          open={open}
          onOpenChange={(open) => {
            if (!open) {
              setFieldValue("isAddingNewAdvocate", false);
            }
            onOpenChange(open);
          }}
        >
          <DialogTrigger asChild>{trigger}</DialogTrigger>
          <DialogContent className="grid max-h-[80vh] min-h-0 grid-rows-[auto_1fr_auto] overflow-hidden">
            <Form className="contents h-full">
              <DialogHeader className="border border-l-0 border-r-0 border-t-0 border-stroke-tertiary bg-green-10">
                <DialogTitle>New Referral</DialogTitle>
                <DialogDescription>
                  Create a new referral for a potential customer.
                </DialogDescription>
              </DialogHeader>

              <div className="min-h-0 overflow-y-auto pb-2">
                <Stack className="h-full p-6" gap="lg" fullWidth>
                  <FormInput
                    label="Services Needed"
                    error={
                      touched.service_needed && values.service_needed
                        ? errors.service_needed
                        : undefined
                    }
                    {...getFieldProps("service_needed")}
                    required
                    fullWidth
                  />
                  <div
                    className={cn(
                      "w-full transition-[height] duration-300 ease-in-out",
                      values.isAddingNewAdvocate ? "h-full" : "h-[56px]",
                    )}
                  >
                    {!values.isAddingNewAdvocate ? (
                      <AdvocateSelector
                        businessId={
                          effectiveBusinessId || userInfo?.parentable_id
                        }
                        value={values.advocate_id}
                        onChange={(value) =>
                          setFieldValue("advocate_id", value)
                        }
                        fullWidth
                        required
                        onAddNew={() =>
                          setFieldValue("isAddingNewAdvocate", true)
                        }
                      />
                    ) : (
                      <Stack gap="lg" fullWidth>
                        <Separator />
                        <Stack
                          direction="row"
                          justify="between"
                          align="center"
                          fullWidth
                        >
                          <Typography variant="subtitleW2">
                            New advocate
                          </Typography>
                          <Typography
                            variant="captionW1"
                            textColor="interactive"
                            role="button"
                            onClick={() =>
                              setFieldValue("isAddingNewAdvocate", false)
                            }
                          >
                            Cancel
                          </Typography>
                        </Stack>
                        <PhoneInput
                          value={values.advocate_phone}
                          onChange={(value) =>
                            setFieldValue("advocate_phone", value)
                          }
                          label="Advocate Phone Number"
                          fullWidth
                          required
                        />
                        <FormInput
                          label="Advocate Name"
                          {...getFieldProps("advocate_name")}
                          fullWidth
                        />
                        <SignupSourceSelector
                          businessId={effectiveBusinessId}
                          selectedSourceId={values.signup_source_id}
                          selectedSourceType={values.signup_source_type}
                          onSourceChange={(sourceId, sourceType) => {
                            setFieldValue("signup_source_id", sourceId);
                            setFieldValue("signup_source_type", sourceType);
                          }}
                        />
                        <FormikCheckboxWithLabel
                          id="onboard"
                          label="Send advocate program sign-up SMS"
                          checked={values.onboard}
                          onCheckedChange={(checked) =>
                            setFieldValue("onboard", !!checked)
                          }
                        />
                        <FormikCheckboxWithLabel
                          id="spiffable"
                          label="Create spiff for attributed user?"
                          checked={
                            values.signup_source_id === "none" ||
                            !values.signup_source_id
                              ? false
                              : values.spiffable
                          }
                          onCheckedChange={(checked) =>
                            setFieldValue("spiffable", !!checked)
                          }
                          disabled={
                            values.signup_source_id === "none" ||
                            !values.signup_source_id
                          }
                        />
                        <Separator />
                      </Stack>
                    )}
                  </div>
                  <Typography variant="subtitleW2" className="text-[#000]">
                    Potential customer
                  </Typography>
                  <FormInput
                    label="Customer Name"
                    error={
                      touched.customer_name && values.customer_name
                        ? errors.customer_name
                        : undefined
                    }
                    {...getFieldProps("customer_name")}
                    fullWidth
                  />
                  <PhoneInput
                    value={values.customer_phone}
                    onChange={(value) => setFieldValue("customer_phone", value)}
                    label="Phone Number"
                    error={
                      touched.customer_phone ? errors.customer_phone : undefined
                    }
                    required
                    fullWidth
                  />
                </Stack>
              </div>

              <DialogFooter className="flex-none">
                <Button
                  variant="tertiary"
                  type="button"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isValid}
                  loading={isSubmitting}
                >
                  Create
                </Button>
              </DialogFooter>
            </Form>
          </DialogContent>
        </Dialog>
      )}
    </Formik>
  );
};

export default AddReferralModal;
